import type { operations } from "@zapier/api-clients/apis/templates-backend/types";

import {
  COPILOT_QUERY_PARAM,
  COPILOT_PROMPT,
} from "../components/TryTemplateButton/TryTemplateButton";

type ExecuteRequestBody =
  operations["templates-execute"]["requestBody"]["content"]["application/json"];

type ExecuteTemplateOptions = {
  templateSlug: string;
  apiHostName?: string;
  copilot?: boolean;
  url?: string;
  referringSource?: ExecuteRequestBody["referringSource"];
  secret?: string;
};

export const useExecuteTemplate = () => {
  const executeTemplate = async ({
    templateSlug,
    apiHostName,
    copilot = false,
    referringSource,
    url = window.location.href,
    secret,
  }: ExecuteTemplateOptions) => {
    try {
      const endpoint = new URL(
        `${apiHostName || ""}/api/templates/execute`,
        window.location.origin,
      );

      // Only set the copilot query param if the copilot flag is true.
      if (copilot) {
        endpoint.searchParams.set("copilot", "true");
      }

      const resp = await fetch(endpoint.href, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          templateSlug,
          referringSource,
          url,
          ...(secret ? { secret } : {}),
        }),
        credentials: "include",
      });

      const body = await resp.json();
      if (!resp.ok) {
        const status = resp.status;
        const error = body.errors
          ? body.errors[0]
          : body.responseBody?.errors?.[0];
        if (error?.code === "service:unknown") {
          throw new Error(
            `Failed to execute (status: ${status}) ${error.error}`,
          );
        }
        throw error || new Error(`HTTP ${status}`);
      }

      const redirectTo = new URL(body.redirectTo, window.location.origin);

      // Only add the default copilot prompt if one isn't already present.
      // The backend may include a dynamic prompt in the redirectTo URL.
      // If the redirectTo URL has a source_template_slug query param, we don't want to add the copilot prompt.
      // As this slug opens a template consumer modal
      if (
        !redirectTo.searchParams.has("source_template_slug") &&
        !redirectTo.searchParams.has(COPILOT_QUERY_PARAM)
      ) {
        redirectTo.searchParams.set(COPILOT_QUERY_PARAM, COPILOT_PROMPT);
      }

      window.location.href = redirectTo.href;
    } catch (error) {
      console.error("Template execution error:", error);
      throw error;
    }
  };

  const DEFAULT_ERROR_MESSAGE =
    "Something went wrong while trying to use this template.";

  const formatExecutionError = (error: {
    code: string;
    status: string;
    title: string;
    detail: string;
  }) => {
    switch (error.code) {
      case "tables:limits:too_many_tables":
      case "limits:too_many_tables": {
        return `You have reached the limit of Tables for your plan. Please upgrade your plan to create more.`;
      }
      case "limits:projects":
      case "interfaces:limits:projects": {
        return `You have reached the limit of Interfaces for your plan. Please upgrade your plan to create more.`;
      }
      case "interfaces:limits:pages": {
        return "You have reached the limit of pages within all Interfaces for your plan. Please upgrade your plan to create more.";
      }
      case "limits:chatbots":
      case "chatbots:limits:chatbots": {
        return `You have reached the limit of Chatbots for your plan. Please upgrade your plan to create more.`;
      }
      case "upstream-error": {
        if (
          error.title === "GulliverApiError" &&
          (error.status === "426" || error.status === "402")
        ) {
          return "This template contains Zap(s) that includes features (steps, paths, premium apps) not covered by your plan. Please upgrade your plan to use this template.";
        }
        return DEFAULT_ERROR_MESSAGE;
      }

      default: {
        return DEFAULT_ERROR_MESSAGE;
      }
    }
  };

  return { executeTemplate, formatExecutionError };
};
